import { getClassroomApiUrl } from "js/utils/getBackendUrl";
import { useUserSessionStore } from "stores/v2/userSessionStore";

/** Pre-canned analysis prompts supported by classroom-ai-service. */
export type AiPromptType = "on_task_analysis" | "recent_class_summary" | "screentime_summary";

export interface AnalyzeRequest {
	applianceId: string;
	studentUsername: string;
	start: string; // RFC3339
	end: string; // RFC3339
	promptType: AiPromptType;
	customPrompt?: string;
	topic?: string; // optional lesson topic/focus to judge on-task against
	students?: string[]; // screentime mode: whole-class aggregate over these usernames
}

export interface AiTimelineEntry {
	start: string;
	end: string;
	activity: string;
	category: string;
	onTask: boolean;
	/** True only when a screenshot visually confirms the activity. */
	screenshotConfirmed: boolean;
	/** True when this was an attempt to reach a site the school web filter blocked. */
	blocked: boolean;
}

export interface AiCategoryCount {
	category: string;
	minutes: number;
}

export interface AiChartData {
	onTaskPercentage: number;
	categoryBreakdown: AiCategoryCount[];
}

/** Deterministic headline metrics for the screentime report. */
export interface AiScreentimeMetrics {
	totalMinutes: number;
	activeFrom: string;
	activeTo: string;
	switches: number;
	longestActivity: string;
	longestMinutes: number;
	blockedAttempts: number;
	onTaskMinutes: number;
	offTaskMinutes: number;
	neutralMinutes: number;
}

/** One row of the whole-class screentime aggregate. */
export interface AiClassStudent {
	student: string;
	totalMinutes: number;
	onTaskMinutes: number;
	offTaskMinutes: number;
	topCategory: string;
	error?: string;
}

export interface AiAnalysisResponse {
	analysisId: string;
	summary: string;
	timeline: AiTimelineEntry[];
	chartData: AiChartData;
	screenshotsCited: number;
	metadata: {
		model: string;
		dataRangeStart: string;
		dataRangeEnd: string;
		screenshotsAnalyzed: number;
		processingTimeMs: number;
	};
	screentime?: AiScreentimeMetrics; // present for the screentime report
	classStudents?: AiClassStudent[]; // present for the whole-class aggregate
}

/**
 * Requests an AI activity overview for a student.
 *
 * The request is routed through the classroom-api BFF
 * (`/v1/appliances/{applianceId}/screenshot-history-analysis`), which validates the teacher
 * session and proxies to classroom-ai-service-poc in-cluster — the AI service
 * has no public ingress. The analysis is multimodal (browsing timeline +
 * screenshots) and can take 10-30s, so callers should show a loading state.
 */
export async function analyzeStudentActivity(req: AnalyzeRequest): Promise<AiAnalysisResponse> {
	const session = useUserSessionStore.getState();
	const baseUrl = getClassroomApiUrl(window.location.hostname);
	const { applianceId, ...body } = req;

	const response = await fetch(`${baseUrl}/v1/appliances/${encodeURIComponent(applianceId)}/screenshot-history-analysis`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: session.getToken() ?? "",
			User: session.getActiveUser() ?? "",
			Device: session.getActiveDevice() ?? "",
		},
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		let detail = "";
		try {
			const body = (await response.json()) as { error?: string };
			detail = body?.error ?? "";
		} catch {
			detail = ""; // response had no JSON body; fall back to the status code below
		}
		throw new Error(detail || `Analysis failed (HTTP ${response.status})`);
	}

	return (await response.json()) as AiAnalysisResponse;
}
